import { ref, computed } from 'vue'
import {
  parseStackTrace,
  findUserCodeFrame,
  diagnoseError,
  generateAIReport,
} from '../utils/errorDiagnostics'

const errors = ref([])
const stats = ref({ total: 0, open: 0, investigating: 0, resolved: 0 })
const isLoading = ref(false)
const lastFetchedAt = ref(null)
const filterStatus = ref(null)
const filterSource = ref(null)

let isCapturing = false

async function postError(errorData) {
  try {
    const res = await fetch('/api/error-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorData),
    })
    return await res.json()
  } catch (e) {
    console.error('[ErrorLog] Failed to post error:', e)
    return null
  }
}

async function fetchErrors() {
  isLoading.value = true
  try {
    const params = new URLSearchParams()
    if (filterStatus.value) params.set('status', filterStatus.value)
    if (filterSource.value) params.set('source', filterSource.value)
    const qs = params.toString()
    const url = '/api/error-log' + (qs ? '?' + qs : '')
    const res = await fetch(url)
    const data = await res.json()
    errors.value = data.errors || []
    lastFetchedAt.value = new Date().toISOString()
  } catch (e) {
    console.error('[ErrorLog] Failed to fetch errors:', e)
  } finally {
    isLoading.value = false
  }
}

async function fetchStats() {
  try {
    const res = await fetch('/api/error-log/stats')
    const data = await res.json()
    stats.value = data
  } catch (e) {
    console.error('[ErrorLog] Failed to fetch stats:', e)
  }
}

async function updateErrorStatus(id, status, resolution = null, resolvedBy = 'dashboard-ai') {
  try {
    const body = { status }
    if (resolution) body.resolution = resolution
    if (status === 'resolved') body.resolvedBy = resolvedBy
    const res = await fetch(`/api/error-log/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json()
    if (data.success) {
      const idx = errors.value.findIndex(e => e.id === id)
      if (idx !== -1) errors.value[idx] = data.error
      await fetchStats()
    }
    return data
  } catch (e) {
    console.error('[ErrorLog] Failed to update error:', e)
    return null
  }
}

/**
 * Strip Vite dev server URL to relative path.
 */
function cleanViteUrl(url) {
  if (!url) return ''
  return url
    .replace(/^https?:\/\/[^/]+\//, '')
    .replace(/\?.*$/, '')
}

/**
 * Auto-detect module name from file path.
 */
function detectModule(filePath) {
  if (!filePath) return ''
  const filename = filePath.split('/').pop() || ''
  return filename.replace(/\.(js|ts|vue)$/, '')
}

/**
 * Enhanced error capture — parses stack trace, extracts file/line,
 * auto-diagnoses, then POSTs to API.
 */
function captureError(error, info = {}) {
  if (isCapturing) return
  isCapturing = true

  // Parse stack trace
  const stackFrames = parseStackTrace(error?.stack)
  const userFrame = findUserCodeFrame(stackFrames)

  // Determine file/line/column from info overrides, window.onerror context, or stack
  const file = info.file
    ? cleanViteUrl(info.file)
    : (info.context?.source ? cleanViteUrl(info.context.source) : '')
      || userFrame?.file
      || ''

  const line = info.line
    || info.context?.lineno
    || userFrame?.line
    || null

  const column = info.column
    || info.context?.colno
    || userFrame?.column
    || null

  // Auto-diagnose
  const diagnosis = diagnoseError(
    error?.name || 'Error',
    error?.message || String(error)
  )

  const errorData = {
    source: 'frontend',
    component: info.component || '',
    function: info.function || '',
    module: info.module || detectModule(file),
    severity: info.severity || 'error',
    type: error?.name || 'Error',
    message: error?.message || String(error),
    stack: error?.stack || '',

    // Enhanced fields
    file,
    line,
    column,
    stackFrames: stackFrames.slice(0, 10),
    diagnosis,

    context: {
      ...info.context,
      url: window.location.href,
      userAgent: navigator.userAgent,
    },
  }

  postError(errorData).finally(() => { isCapturing = false })
}

/**
 * Fetch server-generated AI report (includes code snippets from actual source files).
 */
async function fetchAIReport(id) {
  try {
    const res = await fetch(`/api/error-log/${id}/ai-report`)
    const data = await res.json()
    return data.report || null
  } catch (e) {
    console.error('[ErrorLog] Failed to fetch AI report:', e)
    return null
  }
}

/**
 * Generate AI report client-side (fallback when server is unavailable).
 */
function generateLocalAIReport(errorEntry) {
  return generateAIReport(errorEntry)
}

const openErrors = computed(() => errors.value.filter(e => e.status === 'open'))
const errorCount = computed(() => stats.value.open || 0)
const hasErrors = computed(() => errorCount.value > 0)

export function useErrorLog() {
  return {
    errors,
    stats,
    isLoading,
    lastFetchedAt,
    filterStatus,
    filterSource,
    openErrors,
    errorCount,
    hasErrors,
    fetchErrors,
    fetchStats,
    captureError,
    updateErrorStatus,
    postError,
    fetchAIReport,
    generateLocalAIReport,
  }
}
